import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { APIConfig, isAppVersionOutdated, requestCode } from "../../../../API";
import { isPhoneNumberE164 } from "../../../../phone_number";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { shakeElement } from "../../../../utils/shakeElement";
import AgreementText from "../../../AgreementText";
import { AppButton } from "../../../Generic/AppButton";
import AppModal from "../../../Generic/AppModal/AppModal";
import ControlledCheckbox from "../../../Generic/ControlledCheckbox";
import { If } from "../../../Generic/If";
import Spinner from "../../../Generic/Spinner";
import Question from "../../../OnboardingSection/Question";
import useModal from "../../../useModal";
import AuthForm, { AuthFormParagraph } from "../AuthForm";
import ExternalLink from "../ExternalLink";

interface Props {
  apiConfig: APIConfig;
  onVersionOutdated: () => void;
  onValidSubmit: (data: FormSchemaData) => void;
}

export const enterMobileSchema = z.object({
  phonenumber: z.string().min(1).refine(isPhoneNumberE164),
  agreement: z.literal(true),
});

type FormSchemaData = z.infer<typeof enterMobileSchema>;

const EnterMobileFormStage = (props: Props) => {
  const modal = useModal();
  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormSchemaData>({ resolver: zodResolver(enterMobileSchema) });

  const agreementRef = React.useRef<HTMLDivElement>(null);
  const countTotalFailedAgreements = React.useRef(0);

  const requestCodeMutation = useMutation(
    (phonenumber: string) => requestCode(props.apiConfig, phonenumber),
    {
      onError: (error) =>
        modal.setModalAsError({
          title: translate(STRINGS.ERROR),
          message: `${error}`,
        }),
      onSuccess: (response) => {
        if (isAppVersionOutdated(response)) {
          return props.onVersionOutdated();
        }

        props.onValidSubmit(getValues());
      },
    }
  );

  const onSubmit = handleSubmit(
    (data) => requestCodeMutation.mutate(data.phonenumber),
    ($errors) => {
      if ($errors.agreement !== undefined) {
        shakeElement(agreementRef);

        if (countTotalFailedAgreements.current > 0) {
          modal.setModalAsError({
            title: translate(STRINGS.ERROR),
            message: translate(STRINGS.MUST_AGREE_TERMS),
          });
        }

        countTotalFailedAgreements.current++;
      }
    }
  );

  const handleClickChangedPhoneNumber = () => {
    modal.setModalAsPrompt({
      title: translate(STRINGS.CHANGE_PHONE_NUMBER),
      message: translate(STRINGS.EMAIL_SUPPORT_TO_CHANGE_PHONE_NUMBER),
      actions: [
        {
          key: "email",
          children: translate(STRINGS.EMAIL_SUPPORT),
          onClick: (e) => {
            e.preventDefault();
            window.location.href =
              "mailto:support@mail.medflyt.com?subject=Caregiver changed phone number";
          },
        },
      ],
    });
  };

  return (
    <EnterMobileFormStage.Styled onSubmit={onSubmit}>
      <AuthFormParagraph>{translate(STRINGS.PLEASE_TYPE_IN_YOUR_PHONE_NUMBER)}</AuthFormParagraph>
      <Question.PhoneNumber
        name="phonenumber"
        autoFocus={true}
        disabled={requestCodeMutation.isLoading}
        control={control}
        placeholder={translate(STRINGS.YOUR_MOBILE)}
        data-invalid={errors.phonenumber !== undefined}
      />
      <EnterMobileFormStage.Agreement ref={agreementRef}>
        <ControlledCheckbox
          {...register("agreement")}
          control={control}
          aria-invalid={errors.agreement !== undefined}
        />
        <AgreementText isInvalid={errors.agreement !== undefined}>
          <span>{translate(STRINGS.BY_SIGNING_HERE_I_ACCEPT_THE)}</span>
          <ExternalLink href="https://www.medflyt.com/terms-of-use">
            {translate(STRINGS.TERMS_OF_USE)}
          </ExternalLink>
          <span>{translate(STRINGS.AND_AMPERSAND)}</span>
          <ExternalLink href="https://www.medflyt.com/privacy-policy">
            {translate(STRINGS.PRIVACY_POLICY)}
          </ExternalLink>
        </AgreementText>
      </EnterMobileFormStage.Agreement>
      <AppButton type="submit">
        <If condition={requestCodeMutation.isLoading}>
          <Spinner size={24} />
        </If>
        <span>{translate(STRINGS.SIGN_IN)}</span>
      </AppButton>
      <EnterMobileFormStage.ChangedPhoneNumberParagraph>
        Changed your phone number? <a onClick={handleClickChangedPhoneNumber}>Click here</a>
      </EnterMobileFormStage.ChangedPhoneNumberParagraph>
      <AppModal {...modal.modalProps} />
    </EnterMobileFormStage.Styled>
  );
};

EnterMobileFormStage.Agreement = styled.div`
  display: flex;
  gap: 8px;
`;

EnterMobileFormStage.Styled = styled(AuthForm)`
  ${EnterMobileFormStage.Agreement} {
    margin-bottom: 32px;
  }
`;

EnterMobileFormStage.ChangedPhoneNumberParagraph = styled.p`
  font-size: 14px;
  margin: 0 auto;

  a {
    color: var(--primary-color);
    text-decoration: none;
  }
`;

export default EnterMobileFormStage;
